import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { createEchoInstance } from '../lib/echo';
import { get, post, patch } from '../lib/apiClient';
import { API_URL } from '../_config';

// ---------------------------------------------------------------------------
// TypeScript interfaces
// ---------------------------------------------------------------------------

export interface InboxProvider {
  id: string;
  provider: 'gmail' | 'microsoft365' | 'custom';
  oauth_email: string | null;
  smtp_username: string;
  label: string | null;
}

export interface InboxEmailMessage {
  id: string;
  direction: 'inbound' | 'outbound';
  source: 'workflow_step' | 'manual_reply' | 'imap_sync';
  from_email: string;
  from_name: string | null;
  to_email: string;
  subject: string | null;
  body_html: string | null;
  body_text: string | null;
  sent_at: string;
}

export interface InboxThread {
  id: string;
  contact_email: string;
  contact_name: string | null;
  contact_title: string | null;
  contact_company: string | null;
  subject: string | null;
  last_message_at: string;
  last_message_preview: string | null;
  last_message_by: 'contact' | 'admin' | 'sequence' | null;
  message_count: number;
  sentiment: 'positive' | 'interested' | 'negative' | 'neutral' | 'out_of_office' | null;
  reply_status: 'replied' | 'seen' | 'waiting' | 'closed' | 'auto_paused';
  is_starred: boolean;
  is_seen: boolean;
  campaign_name: string | null;
  workflow_name: string | null;
  sequence_name: string | null;
  sequence_step: number | null;
  variant_label: string | null;
  contact_photo_url: string | null;
  lead: { id: string; full_name: string; company: string | null; title: string | null } | null;
  campaign: { id: string; name: string } | null;
  sender_profile: {
    id: string;
    from_name: string;
    from_email: string;
    email_provider_config: InboxProvider | null;
  } | null;
}

export interface InboxThreadDetail extends InboxThread {
  sequence_on_reply: boolean | null;
  messages: InboxEmailMessage[];
  original_email: {
    subject: string | null;
    email_body: string | null;
    from_email: string;
    from_name: string | null;
    sent_at: string | null;
  } | null;
}

// Legacy alias — keeps any external references to InboxMessage working
export type InboxMessage = InboxThread;

export interface InboxCounts {
  total: number;
  replied: number;
  seen: number;
  waiting: number;
  closed: number;
  auto_paused: number;
}

export interface InboxMeta {
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
  has_more: boolean;
}

export interface InboxListResponse {
  messages: InboxThread[];
  counts: InboxCounts;
}

export interface InboxFilters {
  campaigns: { id: string; name: string }[];
  sequences:  { id: string; name: string }[];
  workflows:  { id: string; title: string }[];
  providers:  InboxProvider[];
}

export interface InboxListParams {
  search?: string;
  status?: string;
  campaign_id?: string;
  sequence_id?: string;
  workflow_id?: string;
  provider_id?: string;
  has_contact_reply?: boolean;
  sort?: string;
  order?: 'asc' | 'desc';
  page?: number;
  per_page?: number;
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useInboxQuery(params: InboxListParams) {
  return useQuery({
    queryKey: ['inbox', params],
    queryFn: () => get<InboxListResponse & { meta: InboxMeta }>(API_URL.INBOX.LIST, params as Record<string, unknown>),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

/**
 * Returns global inbox counts (total / per-status) independent of any tab
 * filter.  Shares the same search/campaign/provider filters as the list but
 * deliberately omits the `status` param so the header counts always reflect
 * the full dataset regardless of which tab is active.
 */
export function useInboxCountsQuery(baseParams: Omit<InboxListParams, 'status' | 'page' | 'per_page' | 'sort' | 'order'>) {
  return useQuery({
    queryKey: ['inbox-counts', baseParams],
    queryFn: () => get<InboxListResponse>(API_URL.INBOX.LIST, { ...baseParams, per_page: 1 } as Record<string, unknown>),
    staleTime: 30_000,
    select: (res) => res.data?.counts,
  });
}

export function useInboxMessageQuery(id: string | null) {
  return useQuery({
    queryKey: ['inbox', id],
    queryFn: () => get<InboxThreadDetail>(API_URL.INBOX.GET(id!)),
    enabled: !!id,
  });
}

export function useInboxFiltersQuery() {
  return useQuery({
    queryKey: ['inbox-filters'],
    queryFn: () => get<InboxFilters>(API_URL.INBOX.FILTERS),
    staleTime: 5 * 60 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useReplyMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: string }) =>
      post<InboxThread>(API_URL.INBOX.REPLY(id), { body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inbox'] });
    },
  });
}

export function useUpdateStatusMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      patch<InboxThread>(API_URL.INBOX.STATUS(id), { status }),
    onMutate: async ({ id, status }) => {
      await queryClient.cancelQueries({ queryKey: ['inbox'] });
      const queries = queryClient.getQueriesData<{ data: InboxListResponse }>({ queryKey: ['inbox'] });
      queries.forEach(([key, cached]) => {
        if (!cached?.data?.messages) return;
        queryClient.setQueryData(key, {
          ...cached,
          data: {
            ...cached.data,
            messages: cached.data.messages.map((m) =>
              m.id === id ? { ...m, reply_status: status } : m
            ),
          },
        });
      });
      return { queries };
    },
    onError: (_err, _vars, context) => {
      context?.queries.forEach(([key, cached]) => {
        queryClient.setQueryData(key, cached);
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['inbox'] });
    },
  });
}

export function useToggleStarMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; is_starred: boolean }) =>
      patch<InboxThread>(API_URL.INBOX.STAR(id), {}),
    onMutate: async ({ id, is_starred }) => {
      await queryClient.cancelQueries({ queryKey: ['inbox'] });
      const queries = queryClient.getQueriesData<{ data: InboxListResponse }>({ queryKey: ['inbox'] });
      queries.forEach(([key, cached]) => {
        if (!cached?.data?.messages) return;
        queryClient.setQueryData(key, {
          ...cached,
          data: {
            ...cached.data,
            messages: cached.data.messages.map((m) =>
              m.id === id ? { ...m, is_starred } : m
            ),
          },
        });
      });
      return { queries };
    },
    onError: (_err, _vars, context) => {
      context?.queries.forEach(([key, cached]) => {
        queryClient.setQueryData(key, cached);
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['inbox'] });
    },
  });
}

export function useMarkReadMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => patch<InboxThread>(API_URL.INBOX.READ(id), {}),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['inbox'] });
    },
  });
}

export function useSyncInboxMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => post<{ dispatched: number }>(API_URL.INBOX.SYNC),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inbox'] });
      queryClient.invalidateQueries({ queryKey: ['inbox-filters'] });
    },
  });
}


// ---------------------------------------------------------------------------
// Real-time inbox updates via Pusher
// ---------------------------------------------------------------------------

/**
 * Subscribe to the private Pusher channel `org.{orgId}` and invalidate the
 * inbox query cache whenever a new reply or an email-open event arrives.
 *
 * Mount this hook once on the Replies page. It connects a single Pusher
 * socket per page mount and cleans up when the component unmounts.
 */
export function useInboxRealtime(
  selectedThreadIdRef?: { current: string | null }
): void {
  const { user, token } = useAuth();
  const queryClient    = useQueryClient();
  const orgId          = user?.organization?.id ?? null;

  useEffect(() => {
    if (!token || !orgId) return;

    try {
      const echo = createEchoInstance(token);
      if (!echo) return; // Pusher not configured — skip real-time silently

      const channelName = `org.${orgId}`;

      // Listen for InboxUpdated events — both replies and email opens
      echo.private(channelName).listen('.InboxUpdated', () => {
        queryClient.invalidateQueries({ queryKey: ['inbox'] });
        queryClient.invalidateQueries({ queryKey: ['inbox-counts'] });
        // Force-refetch the open thread detail immediately so the message list
        // updates without waiting for React Query's background-refetch scheduling.
        const openThreadId = selectedThreadIdRef?.current;
        if (openThreadId) {
          queryClient.refetchQueries({ queryKey: ['inbox', openThreadId], exact: true });
        }
      });

      return () => {
        // Leave the channel but keep the singleton Echo connection alive
        // so re-mounts don't cause a full Pusher reconnect cycle.
        echo.leave(channelName);
      };
    } catch (err) {
      console.warn('[useInboxRealtime] Pusher init failed:', err);
    }
  }, [token, orgId, queryClient]);
}
